import type { ServerActionError } from './get-response';
import { errorToString, getResponse } from './get-response';

/**
 * Generates a URL for fetching the image of a player.
 *
 * @param {number} playerId - The ID of the player.
 * @return {string} The URL for fetching the player image.
 */
export const generatePlayerImageUrl = (playerId: number): string => {
  return `${process.env.NEXT_PUBLIC_NEXT_API_URL}/api/v1/players/image/${playerId}`;
};

/**
 * Generates a URL for fetching the image of a club.
 *
 * @param {number} clubId - The ID of the club.
 * @return {string} The URL for fetching the team image.
 */
export const generateClubImageUrl = (clubId: number, version?: number): string => {
  const url = `${process.env.NEXT_PUBLIC_NEXT_API_URL}/api/v1/club/image/${clubId}`;
  return version ? `${url}?v=${version}` : url;
};
/**
 * Generates a URL with user image.
 * Don't forget to revoke the URL after using it.
 *
 * @param userId
 * @return {string | ServerActionError} The URL with user image or error with message
 */
export const generateUserImageUrl = async (userId: number, isPersonalSettings?: boolean): Promise<string | ServerActionError> => {
  try {
    if (isPersonalSettings) {
      const res = await getResponse('getApiV1PersonalSettingsUserImage');
      // @ts-expect-error Wrong type from in cvs-api
      const blob = await res.blob();
      return URL.createObjectURL(blob);
    }
    const res = await getResponse('getApiV1PhotoUserUserIdImage', userId);

    // @ts-expect-error Wrong type from in cvs-api
    const blob = await res.blob();
    return URL.createObjectURL(blob);
  } catch (error) {
    return { message: errorToString(error) };
  }
};

/**
 * Generates a URL with request attachment.
 * Don't forget to revoke the URL after using it.
 *
 * @param requestId The ID of the request.
 * @return {string | ServerActionError} The URL with request attachment or error with message
 */
export const generateRequestAttachmentUrl = async (requestId: number): Promise<string | ServerActionError> => {
  try {
    const res = await getResponse('getApiV1RequestAttachmentId', requestId);
    // @ts-expect-error Wrong type from in cvs-api
    const blob = await res.blob();
    return URL.createObjectURL(blob);
  } catch (error) {
    return { message: errorToString(error) };
  }
};

export const fetchImageByPhotoId = async (id: number) => {
  try {
    const photo = await getResponse('getApiV1PhotoId', id);
    return `${process.env.NEXT_PUBLIC_NEXT_API_URL}/api/v1${photo.photo}`;
  } catch (err) {
    return { message: errorToString(err) };
  }
};
